'use client'

import { useTranslations } from 'next-intl'

import {
	formatSlotDateTimeRange,
	getFullName,
	getLocaleName,
	getMinimumPatientAge
} from '@/_lib/utils'
import {
	CartProduct,
	CartProductType,
	useOrderFlow
} from '@/app/[locale]/order/_context/order-flow-context'
import { useGetCreditProgram } from '@/app/[locale]/order/_services/useGetCreditProgram'
import { useGetPreventiveExamination } from '@/app/[locale]/order/_services/useGetPreventiveExamination'
import { useGetPriceListItemDetail } from '@/app/[locale]/order/_services/useGetPriceListItemDetail'
import { useGetPromotion } from '@/app/[locale]/order/_services/useGetPromotion'
import { useGetTimeSlotDetail } from '@/app/[locale]/order/_services/useGetTimeSlotDetail'
import { useGetYearProgram } from '@/app/[locale]/order/_services/useGetYearProgram'
import { computeCartDiscountedPrice } from '@cart/_utils/compute-cart-discounted-price'

import { ProductSection } from './a-cart-product-section'
import { CartSkeleton, DoctorCard, type CartVariant } from './a-cart-ui-primitives'

import type { PriceInfo } from '@cart/_hooks/use-cart-prices'

function TemplateCartLine({
	timeslotId,
	variant,
	onPriceReport
}: {
	timeslotId: number
	variant: CartVariant
	onPriceReport?: (info: PriceInfo) => void
}) {
	const t_ageRange = useTranslations('components.ageRange')
	const t_common = useTranslations('common')
	const t_summary = useTranslations('pages.order.summary')
	const { templateCartPricing } = useOrderFlow()

	const { data, isLoading } = useGetTimeSlotDetail(timeslotId)

	if (isLoading) return <CartSkeleton variant={variant} />

	const template = data?.data?.template
	const detail = data?.data?.startTime
		? formatSlotDateTimeRange(data.data.startTime, data.data.endTime)
		: undefined

	const name = template?.name ?? '—'
	const livePricing =
		templateCartPricing !== null && templateCartPricing.timeslotId === timeslotId
			? templateCartPricing
			: null
	const priceFromApi = template?.price ?? null
	const namePrice = livePricing !== null ? livePricing.referencePrice : priceFromApi
	const templateSavings =
		livePricing !== null ? Math.max(0, livePricing.referencePrice - livePricing.finalPrice) : 0

	// Display metadata from the pricing snapshot (set by useTemplateCartPricingSync).
	// Falls back to "Cena" with no prefix when the snapshot is not yet available.
	const priceLabel = livePricing?.priceLabel ?? 'Cena'
	const showFromPrefix = livePricing?.showFromPrefix ?? false

	const employee = template?.employee ?? template?.workplace?.employee ?? null

	const doctorProps = employee
		? {
				photo: employee.photo?.url ?? null,
				name: getFullName(employee),
				position: employee.position,
				ageLabel: t_ageRange('services', {
					ageRange:
						getMinimumPatientAge(
							employee.minimumPatientAge ?? null,
							employee.maximumPatientAge ?? null,
							t_ageRange
						) || ''
				}),
				languagesLabel:
					employee.languages && employee.languages.length > 0
						? `${t_summary('language')} ${employee.languages.map((l: string) => getLocaleName(l, t_common)).join(', ')}`
						: null
			}
		: null

	return (
		<>
			<ProductSection
				name={name}
				namePrice={namePrice}
				savings={templateSavings}
				discountLabel={detail}
				discountLabelTitle={detail ? t_summary('date') : undefined}
				onPriceReport={onPriceReport}
				variant={variant}
				priceLabel={priceLabel}
				showFromPrefix={showFromPrefix}
			/>
			{doctorProps && variant === 'sidebar' && (
				<DoctorCard {...doctorProps} variant='sidebar' />
			)}
		</>
	)
}

function PriceListCartLine({
	itemId,
	variant,
	onPriceReport
}: {
	itemId: number
	variant: CartVariant
	onPriceReport?: (info: PriceInfo) => void
}) {
	const { data, isLoading } = useGetPriceListItemDetail(itemId)

	if (isLoading) return <CartSkeleton variant={variant} />

	const item = data?.data
	const finalPrice = item?.price ?? item?.priceInsurance ?? null
	const hasDiscount =
		item?.priceOld != null &&
		item.priceOld > 0 &&
		finalPrice !== null &&
		item.priceOld > finalPrice
	const namePrice = hasDiscount ? item!.priceOld! : finalPrice
	const savings = hasDiscount ? item!.priceOld! - finalPrice! : 0

	return (
		<ProductSection
			name={item?.name ?? '—'}
			namePrice={namePrice}
			savings={savings}
			onPriceReport={onPriceReport}
			variant={variant}
		/>
	)
}

function AnnualProgramCartLine({
	programId,
	isFamily,
	variant,
	extraModuleIds,
	onPriceReport
}: {
	programId: number
	isFamily: boolean
	variant: CartVariant
	extraModuleIds?: number[]
	onPriceReport?: (info: PriceInfo) => void
}) {
	const { data, isLoading } = useGetYearProgram(programId)

	if (isLoading) return <CartSkeleton variant={variant} />

	const program = data?.data
	const extraModules =
		extraModuleIds && extraModuleIds.length > 0 && program?.modules
			? program.modules.filter(m => extraModuleIds.includes(m.id))
			: []

	if (isFamily) {
		const fullPrice = program?.priceOld ?? program?.price ?? null
		// The 10% family discount applies to the combined price of the program WITHOUT its modules.
		const familySavings = (fullPrice ?? 0) * 0.1
		return (
			<ProductSection
				name={program?.name ?? '—'}
				namePrice={fullPrice}
				familySavings={familySavings}
				showDiscountInline={false}
				modules={extraModules}
				onPriceReport={onPriceReport}
				variant={variant}
			/>
		)
	}

	const { namePrice, savings } = computeCartDiscountedPrice(program)

	return (
		<ProductSection
			name={program?.name ?? '—'}
			namePrice={namePrice}
			savings={savings}
			modules={extraModules}
			onPriceReport={onPriceReport}
			variant={variant}
		/>
	)
}

function PreventiveExamCartLine({
	programId,
	variant,
	extraModuleIds,
	onPriceReport
}: {
	programId: number
	variant: CartVariant
	extraModuleIds?: number[]
	onPriceReport?: (info: PriceInfo) => void
}) {
	const { data, isLoading } = useGetPreventiveExamination(programId)

	if (isLoading) return <CartSkeleton variant={variant} />

	const program = data?.data
	const { namePrice, savings } = computeCartDiscountedPrice(program)
	const extraModules =
		extraModuleIds && extraModuleIds.length > 0 && program?.modules
			? program.modules.filter(m => extraModuleIds.includes(m.id))
			: []

	return (
		<ProductSection
			name={program?.name ?? '—'}
			namePrice={namePrice}
			savings={savings}
			modules={extraModules}
			onPriceReport={onPriceReport}
			variant={variant}
		/>
	)
}

function PromotionCartLine({
	programId,
	variant,
	onPriceReport
}: {
	programId: number
	variant: CartVariant
	onPriceReport?: (info: PriceInfo) => void
}) {
	const { data, isLoading } = useGetPromotion(programId)

	if (isLoading) return <CartSkeleton variant={variant} />

	const { namePrice, savings } = computeCartDiscountedPrice(data?.data)

	return (
		<ProductSection
			name={data?.data?.name ?? '—'}
			namePrice={namePrice}
			savings={savings}
			onPriceReport={onPriceReport}
			variant={variant}
		/>
	)
}

function CreditProgramCartLine({
	programId,
	variant,
	onPriceReport
}: {
	programId: number
	variant: CartVariant
	onPriceReport?: (info: PriceInfo) => void
}) {
	const { data, isLoading } = useGetCreditProgram(programId)

	if (isLoading) return <CartSkeleton variant={variant} />

	return (
		<ProductSection
			name={data?.data?.name ?? '—'}
			namePrice={data?.data?.price ?? null}
			onPriceReport={onPriceReport}
			variant={variant}
		/>
	)
}

/**
 * Picks the cart line implementation for `product.type` and forwards `onPriceReport` when prices resolve.
 */
export function CartItemDisplay({
	product,
	variant,
	onPriceReport
}: {
	product: CartProduct
	variant: CartVariant
	onPriceReport?: (info: PriceInfo) => void
}) {
	switch (product.type) {
		case CartProductType.TEMPLATE:
			return (
				<TemplateCartLine
					timeslotId={product.data.timeslotId}
					variant={variant}
					onPriceReport={onPriceReport}
				/>
			)
		case CartProductType.PRICE_LIST_ITEM:
			return (
				<PriceListCartLine
					itemId={product.data.itemId}
					variant={variant}
					onPriceReport={onPriceReport}
				/>
			)
		case CartProductType.ANNUAL_PROGRAM:
			return (
				<AnnualProgramCartLine
					programId={product.data.programId}
					isFamily={false}
					variant={variant}
					extraModuleIds={product.data.extraModuleIds}
					onPriceReport={onPriceReport}
				/>
			)
		case CartProductType.ANNUAL_PROGRAM_FAMILY:
			return (
				<AnnualProgramCartLine
					programId={product.data.programId}
					isFamily
					variant={variant}
					extraModuleIds={product.data.extraModuleIds}
					onPriceReport={onPriceReport}
				/>
			)
		case CartProductType.PREVENTIVE_EXAM:
			return (
				<PreventiveExamCartLine
					programId={product.data.programId}
					variant={variant}
					extraModuleIds={product.data.extraModuleIds}
					onPriceReport={onPriceReport}
				/>
			)
		case CartProductType.PROMOTION:
			return (
				<PromotionCartLine
					programId={product.data.programId}
					variant={variant}
					onPriceReport={onPriceReport}
				/>
			)
		case CartProductType.CREDIT_PROGRAM:
			return (
				<CreditProgramCartLine
					programId={product.data.programId}
					variant={variant}
					onPriceReport={onPriceReport}
				/>
			)
		default: {
			const _exhaustive: never = product
			return _exhaustive
		}
	}
}
